import { useCallback, useEffect, useRef } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import type { Image3D } from '~/domain/patient/image3d';
import type { Environment } from '~/engine/data/Environment';
import { redactDicomUids } from '~/utils/phiRedaction';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import {
    imageStreamProgressFamily,
    incompleteSeriesUidsState,
} from '~/state/atoms/imageStreamingAtoms';
import {
    setActiveSliceStream,
    clearActiveSliceStream,
} from '~/services/imageStreaming/activeSliceStream';
import {
    SliceStreamController,
    fetchStreamedSlice,
    yieldToEventLoop,
} from '~/services/imageStreaming/SliceStreamController';
import {
    StreamProgressThrottle,
    progressOf,
    type ImageStreamProgress,
} from '~/services/imageStreaming/imageStreamProgress';
import { toStreamTargets } from '~/services/imageStreaming/streamTargets';
import type { SeriesStreamPlan } from './streamingPlan';
import { useOpenNotify } from './useOpenNotify';

export type SliceStreaming = {
    /**
     * Starts filling the progressively loaded images of a completed open,
     * the series `primarySeriesUid` first: it is the image the user is
     * looking at. A no-op when the open produced none.
     */
    startStreaming: (
        images: readonly Image3D[],
        plans: readonly SeriesStreamPlan[],
        primarySeriesUid: string | undefined
    ) => void;
};

/**
 * Owns the post-open slice stream: it starts one when the viewer half of an
 * open has finished loading, and stops it when this hook's owner goes away
 * (the viewer route unmounting, i.e. navigation out of the viewer). Closing a
 * patient stops it too, through `abortActiveSliceStream` in the unload path.
 *
 * Everything the controller touches is injected here: the connector for bytes,
 * the shared `DicomProcessor` for parsing, the environments for the GPU push,
 * and the snackbar for the one warning a permanently incomplete image earns.
 * A slice goes to every grid resident Environment that can draw its volume,
 * as the primary image or as a fused secondary.
 */
export function useSliceStreaming(): SliceStreaming {
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const notify = useOpenNotify();

    // Read through a ref: the controller outlives any one render, and must
    // parse with the processor that exists when a slice lands, not the one
    // that existed when streaming started.
    const dicomProcessorRef = useRef(dicomProcessor);
    useEffect(() => {
        dicomProcessorRef.current = dicomProcessor;
    });

    const controllerRef = useRef<SliceStreamController | null>(null);

    // One throttle per streaming series, plus the atom keys they own, so a
    // stream that ends can both stop emitting and clean up after itself.
    const throttlesRef = useRef(new Map<string, StreamProgressThrottle>());

    const setProgress = useRecoilCallback(
        ({ set }) =>
            (seriesUid: string, progress: ImageStreamProgress): void => {
                set(imageStreamProgressFamily(seriesUid), progress);
                // The set-of-ids atom only moves when a series completes, so
                // its subscribers re-render a few times per load rather than
                // once per throttle tick. An incomplete-final series is never
                // removed: it stays gated for good.
                if (progress.isComplete) {
                    set(incompleteSeriesUidsState, (uids) =>
                        uids.includes(seriesUid)
                            ? uids.filter((uid) => uid !== seriesUid)
                            : uids
                    );
                } else {
                    set(incompleteSeriesUidsState, (uids) =>
                        uids.includes(seriesUid) ? uids : [...uids, seriesUid]
                    );
                }
            },
        []
    );

    const resetProgress = useRecoilCallback(
        ({ reset }) =>
            (seriesUids: readonly string[]): void => {
                for (const seriesUid of seriesUids) {
                    reset(imageStreamProgressFamily(seriesUid));
                }
                reset(incompleteSeriesUidsState);
            },
        []
    );

    /**
     * Ends progress reporting for the current stream: no more atom writes, and
     * the recorded state goes back to "nothing recorded" so a re-open starts
     * from the live domain object rather than a stale counter.
     */
    const disposeProgress = useCallback((): void => {
        const throttles = throttlesRef.current;
        if (throttles.size === 0) {
            return;
        }
        for (const throttle of throttles.values()) {
            throttle.dispose();
        }
        const keys = [...throttles.keys()];
        throttles.clear();
        resetProgress(keys);
    }, [resetProgress]);

    useEffect(() => {
        return () => {
            const controller = controllerRef.current;
            if (controller) {
                controller.abort();
                clearActiveSliceStream(controller);
                controllerRef.current = null;
            }
            disposeProgress();
        };
    }, [disposeProgress]);

    const startStreaming = useCallback(
        (
            images: readonly Image3D[],
            plans: readonly SeriesStreamPlan[],
            primarySeriesUid: string | undefined
        ): void => {
            const targets = toStreamTargets(images, plans, primarySeriesUid);
            if (targets.length === 0) {
                return;
            }

            // A new stream replaces the previous one; its progress entries go
            // with it, or a series that is not re-opened keeps a stale count.
            disposeProgress();

            // Seed before the first fetch: the indication and every gate must
            // be correct from the frame the viewer opens, not from the frame
            // the first slice happens to land.
            const throttles = throttlesRef.current;
            for (const target of targets) {
                const seriesUid = target.image.seriesUid;
                throttles.set(
                    seriesUid,
                    new StreamProgressThrottle((progress) =>
                        setProgress(seriesUid, progress)
                    )
                );
                setProgress(seriesUid, progressOf(target.image));
            }

            // Every Environment that can draw the volume gets the slice. One
            // that throws does not keep it from the others; the failures
            // come back once all of them had their turn.
            const toEveryEnvironment = (
                deliver: (target: Environment) => void
            ): unknown[] => {
                const residents =
                    getDatasetEnvironmentRegistry().residentEnvironments();
                const failures: unknown[] = [];
                for (const target of residents) {
                    try {
                        deliver(target);
                    } catch (error) {
                        failures.push(error);
                    }
                }
                return failures;
            };
            const controller = new SliceStreamController({
                fetchSlice: fetchStreamedSlice,
                parseSlice: (bytes) =>
                    dicomProcessorRef.current.parseImageSliceWithoutStoring(
                        bytes
                    ),
                // The controller contains a throw from a loaded slice.
                onSliceLoaded: (image, zIndex) => {
                    const [failure] = toEveryEnvironment((target) =>
                        target.uploadStreamedSlice(image, zIndex)
                    );
                    if (failure !== undefined) {
                        throw failure;
                    }
                },
                // A write-off is not contained by the controller, so a
                // failure is logged: the slice stays written off in the
                // domain, and only that Environment's rows can lag.
                onSliceUnavailable: (image, zIndex) => {
                    for (const failure of toEveryEnvironment((target) =>
                        target.markStreamedSliceUnavailable(image, zIndex)
                    )) {
                        console.error(
                            'A slice write-off did not reach one view',
                            redactDicomUids(failure)
                        );
                    }
                },
                onProgress: (image) => {
                    // Throttled: intermediate counts are coalesced, the
                    // complete / incomplete-final transitions never are.
                    throttlesRef.current
                        .get(image.seriesUid)
                        ?.report(progressOf(image));
                },
                onSeriesIncomplete: (image) => {
                    // Persistent: an image that will never be complete is not
                    // something to notice in passing.
                    notify(
                        'patientList.open.imageIncomplete',
                        'warning',
                        {
                            missing:
                                image.expectedSliceCount -
                                image.loadedSliceCount,
                            total: image.expectedSliceCount,
                        },
                        true
                    );
                },
                yieldToEventLoop,
            });
            controllerRef.current = controller;
            setActiveSliceStream(controller);
            void controller.run(targets);
        },
        [disposeProgress, notify, setProgress]
    );

    return { startStreaming };
}
